/**
 * Turkish Cart Transfer Utility
 * Handles transferring cart items from Shopify main site to TR subdomain
 */

class TurkishCartTransfer {
  constructor() {
    this.isTurkishContext = this.detectTurkishContext();
  }

  /**
   * Detect if user is in Turkish context
   */
  detectTurkishContext() {
    const path = window.location.pathname;
    const locale = document.documentElement.lang || '';

    return (
      path.includes('/tr') ||
      path.includes('/en-tr') ||
      path.includes('/tr-tr') ||
      locale.startsWith('tr')
    );
  }

  /**
   * Fetch cart data from Shopify
   */
  async fetchCartData() {
    try {
      const response = await fetch('/cart.json');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cartData = await response.json();
      return cartData;
    } catch (error) {
      // console.error('Error fetching cart data:', error);
      return null;
    }
  }

  /**
   * Transform Shopify cart items to simplified TR app format
   */
  transformCartItems(shopifyCart) {
    if (
      !shopifyCart ||
      !shopifyCart.items ||
      !Array.isArray(shopifyCart.items)
    ) {
      return [];
    }

    return shopifyCart.items.map((item, index) => {
      // Extract size and color from variant title, properties, or options
      const size =
        this.extractVariantProperty(item, 'Size') ||
        this.extractVariantProperty(item, 'size') ||
        this.extractFromOptions(item, 'size') ||
        this.extractFromVariantTitle(item, 'size') ||
        'One Size';

      const rawColor =
        this.extractVariantProperty(item, 'Color') ||
        this.extractVariantProperty(item, 'color') ||
        this.extractFromOptions(item, 'color') ||
        this.extractFromVariantTitle(item, 'color') ||
        'Default';

      // Translate color to Turkish if needed
      const color = this.translateColorToTurkish(rawColor);

      return {
        id: `tr-transfer-${item.variant_id}-${index}`, // Unique tracking ID
        handle: item.handle || '', // Product handle (1-to-1 with TR app, contains category)
        size: size,
        color: color,
        quantity: item.quantity,
      };
    });
  }

  /**
   * Extract variant property from item properties
   */
  extractVariantProperty(item, propertyName) {
    if (!item.properties) {
      return null;
    }

    for (const [key, value] of Object.entries(item.properties)) {
      if (key.toLowerCase() === propertyName.toLowerCase()) {
        return value;
      }
    }
    return null;
  }

  /**
   * Extract size/color from item options array
   */
  extractFromOptions(item, type) {
    if (!item.options || !Array.isArray(item.options)) {
      return null;
    }

    // Shopify typically stores options in order: [Size, Color, Material, etc.]
    // We need to determine which option corresponds to size/color
    // This is a heuristic approach - in practice, you might need to check
    // the product's option names from the product data

    const optionValue = item.options[type === 'size' ? 0 : 1]; // Assume size is first, color is second

    if (optionValue) {
      return optionValue;
    }

    return null;
  }

  /**
   * Translate color names to Turkish
   */
  translateColorToTurkish(color) {
    if (!color || color === 'Default') return color;

    const colorTranslations = {
      GOLD: 'Altın',
      GOLDEN: 'Altın',
      SILVER: 'Gümüş',
      SILVERY: 'Gümüş',
      BLACK: 'Siyah',
      WHITE: 'Beyaz',
      RED: 'Kırmızı',
      BLUE: 'Mavi',
      GREEN: 'Yeşil',
      YELLOW: 'Sarı',
      ORANGE: 'Turuncu',
      PURPLE: 'Mor',
      PINK: 'Pembe',
      GRAY: 'Gri',
      GREY: 'Gri',
      BROWN: 'Kahverengi',
      NATURAL: 'Doğal',
      NAVY: 'Lacivert',
      BURGUNDY: 'Bordo',
      MAROON: 'Bordo',
    };

    const upperColor = color.toUpperCase();
    const translatedColor = colorTranslations[upperColor];

    return translatedColor || color;
  }

  /**
   * Extract size/color from variant title (fallback method)
   */
  extractFromVariantTitle(item, type) {
    if (!item.variant_title) return null;

    const title = item.variant_title.toLowerCase();

    const patterns = {
      size: [
        /\b(xs|s|m|l|xl|xxl|xxxl|2xl|3xl)\b/,
        /\b(small|medium|large|extra large)\b/,
        /\b(tek beden|one size)\b/,
        /\b(\d+xl)\b/,
        /\b(\d+l)\b/,
      ],
      color: [
        /\b(red|blue|green|black|white|gray|grey|yellow|orange|purple|pink|natural)\b/,
        /\b(altın|gold|gümüş|silver|bronze|bronz)\b/,
        /\b(kırmızı|mavi|yeşil|siyah|beyaz|gri|sarı|turuncu|mor|pembe)\b/,
        /\b(renk|color)\s*:?\s*([^,\/\s]+)/i,
      ],
    };

    const typePatterns = patterns[type];
    if (!typePatterns) return null;

    for (const pattern of typePatterns) {
      const match = title.match(pattern);
      if (match) {
        const result = match[0].toUpperCase();
        return result;
      }
    }

    return null;
  }

  /**
   * Encode cart data for URL transfer
   */
  encodeCartData(cartItems) {
    try {
      const cartData = {
        items: cartItems,
        timestamp: Date.now(),
      };

      return encodeURIComponent(JSON.stringify(cartData));
    } catch (error) {
      // console.error('Error encoding cart data:', error);
      return null;
    }
  }

  /**
   * Get the appropriate TR subdomain URL based on environment
   */
  getTrSubdomainUrl() {
    // Check if we're in development (localhost) or production
    const isDevelopment =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.includes('localhost');

    return isDevelopment
      ? 'http://localhost:3000'
      : 'https://tr.marvels.tennis';
  }

  /**
   * Generate TR subdomain URL with cart data
   */
  async generateTransferUrl(baseUrl) {
    if (!this.isTurkishContext) {
      return baseUrl;
    }

    const cartData = await this.fetchCartData();
    if (!cartData || !cartData.items || cartData.items.length === 0) {
      return baseUrl;
    }

    const transformedItems = this.transformCartItems(cartData);
    const encodedData = this.encodeCartData(transformedItems);

    if (!encodedData) {
      return baseUrl;
    }

    // Use dynamic TR subdomain URL
    const trBaseUrl = this.getTrSubdomainUrl();
    const path = baseUrl.includes('/cart') ? '/cart' : '/checkout';
    const fullUrl = `${trBaseUrl}${path}`;

    // Add cart data as URL parameter
    const finalUrl = `${fullUrl}?cart_data=${encodedData}`;

    return finalUrl;
  }

  /**
   * Handle cart/checkout button clicks for Turkish visitors
   */
  async handleTurkishRedirect(event, targetUrl) {
    if (!this.isTurkishContext) {
      return; // Let normal behavior proceed
    }

    event.preventDefault();
    event.stopPropagation();

    // Clear any existing timeout
    const button = event.target.closest('button, a');
    if (button) {
      const timeoutId = button.getAttribute('data-timeout-id');
      if (timeoutId) {
        clearTimeout(parseInt(timeoutId));
        button.removeAttribute('data-timeout-id');
      }
    }

    try {
      const transferUrl = await this.generateTransferUrl(targetUrl);

      // Show loading state if possible
      if (button) {
        const originalText = button.textContent;
        button.textContent = 'Yönlendiriliyor...';
        button.disabled = true;

        // Reset button after redirect
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
      }

      // Redirect to TR subdomain with cart data
      window.location.href = transferUrl;
    } catch (error) {
      // console.error('Error handling Turkish redirect:', error);
      // Fallback to normal redirect
      window.location.href = targetUrl;
    }
  }
}

// Initialize the cart transfer utility
const turkishCartTransfer = new TurkishCartTransfer();

// Set up event listeners for Turkish redirect buttons
document.addEventListener('DOMContentLoaded', function () {
  // Handle buttons with data-turkish-redirect attribute
  // Use both click and touchstart for better mobile compatibility
  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-turkish-redirect]');
    if (button) {
      const targetUrl = button.getAttribute('data-turkish-redirect');
      if (targetUrl) {
        turkishCartTransfer.handleTurkishRedirect(event, targetUrl);
      }
    }
  });

  // Add touchstart event for mobile devices
  document.addEventListener(
    'touchstart',
    function (event) {
      const button = event.target.closest('[data-turkish-redirect]');
      if (button) {
        // Prevent default touch behavior to avoid double-firing
        event.preventDefault();
        const targetUrl = button.getAttribute('data-turkish-redirect');
        const fallbackUrl = button.getAttribute('data-fallback-url');
        if (targetUrl) {
          // Add timeout fallback for mobile devices
          const timeoutId = setTimeout(() => {
            if (fallbackUrl) {
              window.location.href = fallbackUrl;
            }
          }, 1000); // 1 second timeout

          // Store timeout ID for cleanup
          button.setAttribute('data-timeout-id', timeoutId);

          turkishCartTransfer.handleTurkishRedirect(event, targetUrl);
        }
      }
    },
    { passive: false },
  );
});

// Export for use in other scripts
window.TurkishCartTransfer = TurkishCartTransfer;
window.turkishCartTransfer = turkishCartTransfer;
